import React, { useRef, Fragment, useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useFormikContext } from 'formik';
import type { State as UserInfoType } from 'Store/user-info/reducers/userInfoReducer';
import type { AttendeesType } from 'Store/booking/attendees/attendees.selector';
import 'Store/booking/attendees/attendees.selector';
import { useAppContext } from 'Context/ReactAppContext';
import { useAutoScrollToError } from 'Components/frameworks/formik/ScrollToErrorHook';
import { ICONS } from 'Components/utils/icon/icons';
import { SimpleNotification } from 'Components/frameworks/notification/SimpleNotification';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { t } from 'Src/i18n';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { MultiCGUBlock } from './contact-info-form/MultiCGUBlock';
import { ACCEPTED_CGUS } from './constants';
import AttendeesForms from './AttendeesForms';
import { trackAttendeesFormError } from './ContactInfoUtils';
type Props = {
  proposal: Proposal;
  connectedUserInfo: UserInfoType;
  hasAddress: boolean;
  i18n: I18N;
  attendees: {
    list: AttendeesType['all'];
    adults: AttendeesType['adults'];
    children: AttendeesType['children'];
  } & {
    gmNumber: string;
    key: number;
  };
  setErrorMessage: (arg0: string) => void;
  setHasUnselectedTravelCompanions: (arg0: boolean) => void;
};
export const ContactInfoForm = ({
  proposal,
  connectedUserInfo,
  hasAddress,
  i18n,
  attendees,
  setErrorMessage,
  setHasUnselectedTravelCompanions,
}: Props) => {
  const formik = useFormikContext();
  // we need a ref for formik for the unmount to have access to the formik bag (the formik bag ref change at every render 😭)
  const formikRef = useRef();
  const { isUserLogged } = useAppContext();
  const [hasMainAttendeeErrors, setHasMainAttendeesError] = useState(false);
  const [hasPerformValidation, setHasPerformValidation] = useState(false);
  useAutoScrollToError(formik);
  const { trackEvent } = useTrackEvent();
  formikRef.current = formik;
  useAutoScrollToError(formik);
  const { arrivalDate } = proposal;
  const validationCallback = useCallback(() => {
    formik.submitForm();
    const legalAgreementError = formik.errors?.adults?.[0]?.optinsCGU;
    const hasLegalAgreementError =
      legalAgreementError !== null && legalAgreementError !== undefined;

    if (hasLegalAgreementError) {
      setHasMainAttendeesError(true);
      setErrorMessage(t('booking.participants.errorMessage.legalAgreement'));
    }

    trackAttendeesFormError(trackEvent, formik);

    if (isUserLogged) {
      const hasError = formik.errors.adults?.slice(1).some((adult) => !!adult);

      if (hasError) {
        if (hasLegalAgreementError) {
          setHasMainAttendeesError(true);
          setErrorMessage(t('booking.participants.errorMessage.legalAgreement'));
        }

        setErrorMessage(t('booking.participants.errorMessage.travelCompanionsNotSelected'));
      }

      setHasUnselectedTravelCompanions(hasError);
    }

    return Promise.reject();
  }, [formik.errors]);
  useBookingNavbar({
    validationCallback,
  });
  useEffect(() => {
    /* We need to validate the form when the user is connected and the form is displayed before showing the preview */
    formik.validateForm().then((errors) => {
      // @ts-expect-error TODO(flow-migration)
      if (!isEmpty(errors) && !isEmpty(errors.adults?.[0])) {
        setHasMainAttendeesError(true);
      }

      setHasPerformValidation(true);
    });
  }, []);
  return (
    <Fragment>
      <div>
        <SimpleNotification
          id="ContactInfoFormInformations"
          title={t('booking.participants.personnalInfosHeader.identityPapersMatchingTitle')}
          content={t('booking.participants.personnalInfosHeader.identityPapersMatching')}
          iconName={ICONS.EXCLAMATION_POINT}
        />
        <AttendeesForms
          list={attendees.list}
          arrivalDate={arrivalDate}
          connectedUserInfo={connectedUserInfo}
          hasMainAttendeeErrors={hasMainAttendeeErrors}
          hasPerformValidation={hasPerformValidation}
          hasAddress={hasAddress}
          i18n={i18n}
        />
      </div>
      <MultiCGUBlock
        i18n={i18n}
        idsCGU={
          ACCEPTED_CGUS // ids of the CGUs to show in the block
        }
      />
    </Fragment>
  );
};
